import React, { useEffect, useState } from "react";
import Header from "@user/components/Header";
import Footer from "@user/components/Footer";
import { LawyerCard, LawyerCardSkeleton } from "@user/components/LawyerCard";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Search, Filter } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function Lawyers() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [lawyers, setLawyers] = useState([]);
  const [cities, setCities] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [langs, setLangs] = useState([]);
  const [city, setCity] = useState("all");
  const [spec, setSpec] = useState(searchParams.get("spec") || "all");
  const [lang, setLang] = useState("all");
  const [availableOnly, setAvailableOnly] = useState(false);
  const [liveOnly, setLiveOnly] = useState(false);
  const [livePulses, setLivePulses] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {
        city: city !== "all" ? city : undefined,
        specialization: spec !== "all" ? spec : undefined,
        language: lang !== "all" ? lang : undefined,
        available_only: availableOnly || undefined,
      };
      const [lr, mr, pr] = await Promise.all([
        api.get("/lawyers", { params }),
        api.get("/lawyers-meta"),
        api.get("/pulses/active", {
          params: {
            specialization: spec !== "all" ? spec : undefined,
            city: city !== "all" ? city : undefined,
          },
        }),
      ]);
      setLawyers(lr.data);
      setCities(mr.data.cities);
      setSpecs(mr.data.specializations);
      setLangs(mr.data.languages);
      setLivePulses(pr.data?.items || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [city, spec, lang, availableOnly]);

  // Re-fetch active pulses every 30s so the banner stays accurate.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const r = await api.get("/pulses/active", {
          params: {
            specialization: spec !== "all" ? spec : undefined,
            city: city !== "all" ? city : undefined,
          },
        });
        setLivePulses(r.data?.items || []);
      } catch {}
    }, 30_000);
    return () => clearInterval(id);
  }, [spec, city]);

  // Merge live pulse data into lawyers list so cards know about it
  const pulseById = React.useMemo(() => {
    const m = {};
    (livePulses || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [livePulses]);

  const enrichedLawyers = lawyers.map((l) => pulseById[l.id] ? { ...l, ...pulseById[l.id] } : l);

  const filtered = enrichedLawyers.filter((l) => {
    const matchesQ = !q.trim() || (l.name + l.specialization + l.city + (l.bio || "")).toLowerCase().includes(q.toLowerCase());
    const matchesLive = !liveOnly || !!pulseById[l.id];
    return matchesQ && matchesLive;
  });

  const onBook = (l) => {
    if (!user) { login(); return; }
    navigate(`/book/${l.id}`);
  };

  const onInstant = (l) => {
    if (!user) { login(); return; }
    navigate(`/book/${l.id}?mode=instant`);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="mb-6">
          <span className="adv-chip">Verified Lawyers</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
            Find the right lawyer for you
          </h1>
          <p className="mt-2 text-slate-600 max-w-2xl">Verified profiles · Transparent pricing · Available in your language.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, keyword…"
              data-testid="lawyer-search-input"
              className="pl-9"
            />
          </div>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger className="md:w-[160px]" data-testid="lawyer-filter-city"><SelectValue placeholder="City" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All cities</SelectItem>
              {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={spec} onValueChange={setSpec}>
            <SelectTrigger className="md:w-[180px]" data-testid="lawyer-filter-spec"><SelectValue placeholder="Practice" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All practice</SelectItem>
              {specs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="md:w-[160px]" data-testid="lawyer-filter-lang"><SelectValue placeholder="Language" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All languages</SelectItem>
              {langs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 px-3 bg-slate-50 rounded-md">
            <Switch id="avail" checked={availableOnly} onCheckedChange={setAvailableOnly} data-testid="toggle-available" />
            <Label htmlFor="avail" className="text-xs font-semibold whitespace-nowrap cursor-pointer">Available now</Label>
          </div>
          <div className="flex items-center gap-2 px-3 bg-emerald-50 rounded-md border border-emerald-200">
            <Switch id="live" checked={liveOnly} onCheckedChange={setLiveOnly} data-testid="toggle-live-now" />
            <Label htmlFor="live" className="text-xs font-bold whitespace-nowrap cursor-pointer text-emerald-800 inline-flex items-center gap-1.5">
              <span className="relative inline-flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-600" />
              </span>
              Live now
            </Label>
          </div>
        </div>

        {livePulses.length > 0 && !liveOnly && (
          <div
            className="mt-4 bg-gradient-to-r from-emerald-50 to-white border border-emerald-200 rounded-2xl p-3 sm:p-4 flex flex-wrap items-center gap-3"
            data-testid="live-pulse-banner"
          >
            <div className="flex items-center gap-2">
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600" />
              </span>
              <p className="text-sm font-bold text-emerald-900">
                {livePulses.length} lawyer{livePulses.length === 1 ? "" : "s"} available <strong>right now</strong>
                {spec !== "all" ? <> in {spec}</> : null}
                {city !== "all" ? <> · {city}</> : null}
              </p>
            </div>
            <button
              onClick={() => setLiveOnly(true)}
              data-testid="show-live-only-btn"
              className="ml-auto px-3 py-1.5 text-xs font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
            >
              Show only live →
            </button>
          </div>
        )}

        <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="lawyers-grid">
          {loading && Array.from({ length: 6 }).map((_, i) => <LawyerCardSkeleton key={i} />)}
          {!loading && filtered.length === 0 && (
            <div className="md:col-span-2 xl:col-span-3 text-center py-16">
              <Filter size={36} className="mx-auto text-slate-300" />
              <p className="mt-3 text-slate-500">No lawyers match your filters. Try clearing them.</p>
            </div>
          )}
          {!loading && filtered.map((l) => <LawyerCard key={l.id} lawyer={l} onBook={onBook} onInstant={onInstant} />)}
        </div>
      </div>
      <Footer />
    </div>
  );
}
